import { useCallback, useEffect, useRef } from 'react';
import posthog from 'posthog-js';

const POSTHOG_KEY = process.env.REACT_APP_POSTHOG_KEY || '';
const POSTHOG_HOST = process.env.REACT_APP_POSTHOG_HOST || 'https://us.i.posthog.com';

let initialized = false;

// The token after `/shared/` is a capability: whoever holds it can open (and
// fork) the plan. It must not leave the device inside any analytics property.
// The router's own param semantics: after `/shared/`, ANY run of non-slash
// characters is the token — `?`, `#`, `&` and whitespace included, because
// an encoded one of those (`%3F`, `%C2%A0`) is a legal parameter character
// once decoded. Extra slashes are tolerated (`%2F` decodes to one; the
// router keeps routing). A literal `?`/`#` in the RAW string is URL
// structure and is split off BEFORE decoding, so a plain query survives.
const SHARE_SEGMENT = /(\/shared\/+)([^/]+)/gi;
const SHARE_SEGMENT_TEST = /\/shared\/+[^/]+/i;
const JSON_ESCAPE = /\\u[0-9a-fA-F]{4}/;
const PERCENT_RUN = /(?:%[0-9A-Fa-f]{2})+/g;

/**
 * Percent-decode each RUN of `%XX` sequences on its own. A whole-string
 * `decodeURIComponent` throws on the first malformed sequence anywhere — so
 * a hostile query (`?bad=%E0%A4%A`) shielded an encoded pathname
 * (`/sh%61red/…`) that the router still decoded and routed. Per run, garbage
 * in one place cannot stop decoding in another. A run that is not valid
 * UTF-8 stays as it is: the router refuses to route a pathname containing
 * one, so nothing that reaches the share page is hidden behind it.
 */
function percentDecodeTolerant(input: string): string {
  return input.replace(PERCENT_RUN, (run) => {
    try {
      return decodeURIComponent(run);
    } catch {
      return run;
    }
  });
}

/**
 * Decode a string to a fixed point: percent-encoding (any depth, per run)
 * and JSON `\uXXXX` escapes, each step tolerant of malformed input.
 * Bounded, because a hostile value could otherwise nest forever.
 */
function decodeToFixedPoint(input: string): string {
  let current = input;
  for (let i = 0; i < 8; i++) {
    let next = percentDecodeTolerant(current);
    if (JSON_ESCAPE.test(next)) {
      try {
        next = JSON.parse('"' + next.replace(/\\(?!u[0-9a-fA-F]{4})/g, '\\\\').replace(/"/g, '\\"') + '"');
      } catch {
        // not a decodable escape sequence; keep as is
      }
    }
    if (next === current) break;
    current = next;
  }
  return current;
}

/**
 * Redact share tokens from every string in a properties object, recursively.
 *
 * The share page's URL IS the capability, and posthog-js attaches
 * `$current_url`, `$pathname`, `$referrer` and the `$set_once` initial-URL
 * person properties to every capture on its own — so an event from
 * `/shared/<token>` carried the token whatever the caller passed.
 *
 * Matched on MEANING, not shape: a route reaches `/shared/:token` however
 * its path is encoded (React Router decodes segments before matching), and
 * a shape-based denylist was evaded by `/sh%61red/…`, by triple encoding, by
 * mixed separators and by literal `/` escapes in the same round of
 * review. So the string is decoded to a fixed point first; if the decoded
 * form carries a share segment, the DECODED, redacted string is returned —
 * the encoding was never analytics data. Strings without one are returned
 * exactly as they came. Installed as `sanitize_properties`, which runs on
 * every capture including `$pageview`. Exported so the boundary is bound by
 * a test, not by trust.
 */
export function redactShareTokens<T>(value: T): T {
  return redactWalk(value, new WeakSet<object>(), 0);
}

/**
 * One string. Split at the RAW first `?` or `#` — that is URL structure the
 * browser itself honours, so a token there was never part of the path —
 * then decode each part to a fixed point and redact on the router's param
 * semantics. A part with no share segment is returned exactly as it came;
 * a part with one is returned decoded and redacted.
 */
function redactString(value: string): string {
  const cut = value.search(/[?#]/);
  const head = cut < 0 ? value : value.slice(0, cut);
  const tail = cut < 0 ? '' : value.slice(cut);
  const decodedHead = decodeToFixedPoint(head);
  const decodedTail = tail ? decodeToFixedPoint(tail) : '';
  const inHead = SHARE_SEGMENT_TEST.test(decodedHead);
  const inTail = tail !== '' && SHARE_SEGMENT_TEST.test(decodedTail);
  if (!inHead && !inTail) return value;
  return (
    (inHead ? decodedHead.replace(SHARE_SEGMENT, '$1[token]') : head) +
    (inTail ? decodedTail.replace(SHARE_SEGMENT, '$1[token]') : tail)
  );
}

// Bounded two ways, because `sanitize_properties` runs on every capture and
// must never take the page down: a depth bound for deep acyclic values, and
// a seen-set for cycles — a depth bound alone lets `{a: root, b: root}`
// expand to ~2^32 objects before the cut. The seen-set is walk-wide, so an
// object referenced twice is emitted once and `[omitted]` after; PostHog
// properties are JSON-shaped, and JSON has no shared references to lose.
const MAX_DEPTH = 32;

function redactWalk<T>(value: T, seen: WeakSet<object>, depth: number): T {
  if (typeof value === 'string') return redactString(value) as unknown as T;
  if (value && typeof value === 'object') {
    if (depth >= MAX_DEPTH || seen.has(value as object)) return '[omitted]' as unknown as T;
    seen.add(value as object);
    if (Array.isArray(value)) {
      return value.map((v) => redactWalk(v, seen, depth + 1)) as unknown as T;
    }
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) out[k] = redactWalk(v, seen, depth + 1);
    return out as unknown as T;
  }
  return value;
}

/**
 * Initialize PostHog analytics. Call once at app startup.
 * No-ops if REACT_APP_POSTHOG_KEY is not set.
 */
export function initAnalytics(): void {
  if (initialized) return;
  // Log status in console (works even without key) so we can verify env var
  if (typeof window !== 'undefined') {
    console.log('[analytics] init — key present:', !!POSTHOG_KEY, 'host:', POSTHOG_HOST);
  }
  if (!POSTHOG_KEY) return;
  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    autocapture: false, // manual events only for now
    capture_pageview: false, // we handle page views manually
    persistence: 'localStorage',
    // Every capture, including the SDK's own URL-derived properties.
    sanitize_properties: (properties) => redactShareTokens(properties),
    // The SDK persists the RAW initial URL before `sanitize_properties` ever
    // runs, and the one request that ships it unsanitized is `/flags`
    // (person_properties on a flags reload after identify). We use no
    // feature flags, surveys or recording, so refuse that endpoint outright
    // — `_shouldDisableFlags()` then never calls it — rather than trust a
    // sanitizer that path does not go through.
    advanced_disable_flags: true,
    // The repo's rule (POSTHOG_NEXT_MOVES.md): replay stays off. Enforced
    // here, not assumed from the default.
    disable_session_recording: true,
  });
  initialized = true;
}

/**
 * Identify a user for analytics tracking.
 */
export function identifyUser(userId: number, properties?: Record<string, unknown>): void {
  if (!POSTHOG_KEY) return;
  posthog.identify(String(userId), properties);
}

/**
 * Reset analytics identity (on logout).
 */
export function resetAnalytics(): void {
  if (!POSTHOG_KEY) return;
  posthog.reset();
}

/**
 * Track a named event with optional properties.
 */
export function trackEvent(event: string, properties?: Record<string, unknown>): void {
  if (!POSTHOG_KEY) return;
  posthog.capture(event, properties);
}

// ---- Key events from spec §20.2-20.3 ----

// Discovery
export const trackPlanViewed = (planId: number, source: string) =>
  trackEvent('plan_viewed', { plan_id: planId, source });

// A first-screen starter tapped (auto-starters decision 9): the public plan
// slug and its 1-based row, sent before navigation. No share token exists on
// that surface.
export const trackStarterOpened = (slug: string, rank: number) =>
  trackEvent('starter_opened', { slug, rank });

export const trackPlanForked = (planId: number, destination: string) =>
  trackEvent('plan_forked', { plan_id: planId, destination });

export const trackDiscoveryFilterApplied = (filters: Record<string, unknown>) =>
  trackEvent('discovery_filter_applied', filters);

export const trackMatchMeCompleted = (answers: Record<string, unknown>) =>
  trackEvent('match_me_completed', answers);

// Chat
export const trackChatMessageSent = (context: string, model: string) =>
  trackEvent('chat_message_sent', { context, model });

export const trackChatSoftGateHit = (messageCount: number) =>
  trackEvent('chat_soft_gate_hit', { message_count: messageCount });

// Conversion
export const trackUpgradeCtaClicked = (source: string) =>
  trackEvent('upgrade_cta_clicked', { source });

export const trackSubscriptionStarted = (plan: string) =>
  trackEvent('subscription_started', { plan });

// Atlas — destination funnel (spec §22.6).
// NOTE: no raw query text, ever. People type their own names and their kids'
// schools into search boxes, and a query stream in a third-party product
// cannot be walked back. Events carry the OUTCOME and the query's length; the
// exact text of a miss lives server-side, owner-only, for 90 days.
export const trackDestinationSearched = (
  outcome: 'hit' | 'ambiguous' | 'miss',
  queryType: 'place_like' | 'free_text',
  queryLen: number,
  resolvedSlug: string | null,
) => trackEvent('destination_searched', {
  outcome, query_type: queryType, query_len: queryLen, resolved_slug: resolvedSlug,
});

export const trackDestinationViewed = (
  slug: string, edition: number | null, source: string, isCurrent: boolean,
) => trackEvent('destination_viewed', {
  slug, edition, source, is_current_edition: isCurrent,
});

export const trackDestinationShared = (
  slug: string, edition: number | null, method: string,
) => trackEvent('destination_shared', { slug, edition, method });

/**
 * A guide kept for later (spec §22.6, proposal §09: "destination slug and
 * guide edition ride on view, save, fork, and share events").
 */
export const trackDestinationSaved = (
  slug: string, edition: number | null, source: string,
) => trackEvent('destination_saved', { slug, edition, source });

/**
 * A save given dates — this is the moment a real plan exists, so it emits
 * `plan_created` like every other creation path. Without it the atlas route
 * would be invisible in the land→first-plan funnel, which is the number the
 * distribution test turns on.
 */
export const trackSavedTripScheduled = (
  slug: string, edition: number | null,
) => {
  trackEvent('plan_created', { source: 'saved_trip', anonymous: false });
  trackEvent('saved_trip_scheduled', { slug, edition });
};

// Plan from a photo (docs/CAPTURE_TO_PLAN_PLAN.md §7). No image data, file
// names or extracted text, ever: `destination` and `highlights` are never
// properties. Every prop has a closed domain, and a helper handed a value
// outside it drops the WHOLE event rather than send it.
export const CAPTURE_EVENT_DOMAINS = {
  action: ['sign_up', 'dismissed'],
  reason: ['unavailable', 'rate_limited', 'too_large', 'unsupported', 'error'],
  source_kind: ['booklet', 'magazine', 'email', 'website', 'ad', 'billboard', 'other', 'none'],
  trip_type: ['city', 'beach', 'nature', 'road_trip', 'cruise', 'resort', 'multi_city', 'other'],
  season: ['spring', 'summer', 'autumn', 'winter', 'any'],
} as const;

export type CaptureFailureReason = typeof CAPTURE_EVENT_DOMAINS.reason[number];

const inDomain = (domain: readonly string[], value: unknown): boolean => (
  typeof value === 'string' && domain.includes(value)
);
const intInRange = (value: unknown, min: number, max: number): boolean => (
  typeof value === 'number' && Number.isInteger(value) && value >= min && value <= max
);

export const trackCaptureSignUpPrompted = (action: 'sign_up' | 'dismissed'): void => {
  if (!inDomain(CAPTURE_EVENT_DOMAINS.action, action)) return;
  trackEvent('capture_sign_up_prompted', { action });
};

export const trackCaptureIdeasFound = (
  sourceCount: number, ideaCount: number, sourceKind: string,
): void => {
  if (!intInRange(sourceCount, 1, 4) || !intInRange(ideaCount, 0, 6)) return;
  if (!inDomain(CAPTURE_EVENT_DOMAINS.source_kind, sourceKind)) return;
  trackEvent('capture_ideas_found', {
    source_count: sourceCount, idea_count: ideaCount, source_kind: sourceKind,
  });
};

export const trackCaptureFailed = (reason: CaptureFailureReason): void => {
  if (!inDomain(CAPTURE_EVENT_DOMAINS.reason, reason)) return;
  trackEvent('capture_failed', { reason });
};

export const trackCaptureIdeaPlanned = (
  ideaCount: number, tripType: string, season: string, hasDays: boolean,
): void => {
  if (!intInRange(ideaCount, 0, 6) || typeof hasDays !== 'boolean') return;
  if (!inDomain(CAPTURE_EVENT_DOMAINS.trip_type, tripType)) return;
  if (!inDomain(CAPTURE_EVENT_DOMAINS.season, season)) return;
  trackEvent('capture_idea_planned', {
    idea_count: ideaCount, trip_type: tripType, season, has_days: hasDays,
  });
};

// Re-engagement
export const trackIntelEmailSubscribed = () =>
  trackEvent('intel_email_subscribed');

export const trackTripBriefingViewed = (destination: string) =>
  trackEvent('trip_briefing_viewed', { destination });

/**
 * Route-level page view. Wired to router location changes in App so every
 * navigation records a $pageview (posthog-js fills $current_url itself).
 */
export function trackPageView(path: string): void {
  if (!POSTHOG_KEY) return;
  // RouteAnalytics is a child of App, and child effects run before parent
  // effects — self-initialize (idempotent) so the landing pageview isn't lost.
  initAnalytics();
  posthog.capture('$pageview', { path });
}

/**
 * Hook for page view tracking. Fires on mount and path changes.
 */
export function usePageView(pageName: string): void {
  const lastPage = useRef('');
  useEffect(() => {
    if (!POSTHOG_KEY || pageName === lastPage.current) return;
    lastPage.current = pageName;
    posthog.capture('$pageview', { page: pageName });
  }, [pageName]);
}

/**
 * Hook that returns a stable trackEvent function.
 */
export function useAnalytics() {
  const track = useCallback((event: string, properties?: Record<string, unknown>) => {
    trackEvent(event, properties);
  }, []);

  return { track, trackEvent, identifyUser, resetAnalytics };
}
